// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2024-11-28 19:32:20 +0100 (Thu, 28 Nov 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 147820 $
//  Purpose   : job reports
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Loader, Button, Container, Segment, Icon, Label, Progress} from 'semantic-ui-react'
import {matchSorter} from 'match-sorter'
import {differenceInSeconds, parseISO} from 'date-fns'

// ---Framework imports
import {get, size, isNil, dict, mapItems, isArray, groupBy} from 'ldash'
import log from 'uix/logging'
import {Sel} from 'uix/registries'
import {
  useSelector,
  useState,
  useMemo,
  usePersistent,
  useResettableState,
  useEffect,
  useChunk,
} from 'uix/hooks'
import {SelectableTable, AutoPortal, Link, XMenu, ResizableDialog, AutoSizer} from 'uix/components'
import {JobTemplates} from 'uix/registry'
import {TranslatedTable} from 'uix/components'
import {formatDateTime, formatTime, ucFirst, formatSeconds} from 'uix/utils'

// ---Local imports
import {useJobEvents} from './hooks'
import {state_icons} from './utils'
import style from './styles.scss'

// ---Exports

let buildCounter = (name, count) => {
  return (
    <Label
      color={count ? event_icons[name].color : 'grey'}
      style={{alignItems: 'center', display: 'flex'}}
      content={`${name} ${count || 0} `}
      icon={event_icons[name].icon}
      key={name}
      size="tiny"
      basic
      active={true}
    />
  )
}

const event_icons = {
  info: {color: 'black', icon: 'info'},
  event: {color: 'black', icon: 'bookmark'},
  warn: {color: 'orange', icon: 'warning circle'},
  error: {color: 'red', icon: 'alarm'},
}

let movingAverage = (history, window_size) =>
  /* history is given as list [{speed, ...}]
  add percentage_avg attribute to list by calculating moving average of speed
  with sliding integer window_size
  */

  history.map((data, index) => {
    let start = Math.max(0, index - window_size + 1)
    let window = history.slice(start, index + 1).map(item => item.speed)
    let speed_avg = window.reduce((total, num) => total + num) / window.length
    return {...data, speed_avg}
  })

const Timings = ({history, movAvg}) => {
  let Recharts = useChunk('Recharts')
  if (!Recharts) return null
  let {BarChart, Bar, XAxis, YAxis, Tooltip} = Recharts

  let total = history[0].total
  let data = history
    .map((h, i) => {
      let [delta_perc, delta_sec] = [0, 0]
      if (i > 0) {
        delta_perc = h.percentage - history[i - 1].percentage
        delta_sec = differenceInSeconds(h.ts, history[i - 1].ts)
      }
      if (delta_sec) {
        let delta = Math.ceil((delta_perc / 100) * total)
        let delta_per_sec = delta / delta_sec

        return {speed: delta_per_sec, ts: formatTime(h.ts), delta, delta_sec}
      }
    })
    .filter(Boolean)

  data = movingAverage(data, movAvg)
  return (
    <AutoSizer>
      <BarChart width={150} height={40} data={data}>
        <Tooltip
          formatter={(value, name, props) =>
            name == 'speed_avg' ? `${value.toFixed(1)} per sec (${props.payload.delta})` : value
          }
        />
        <Bar dataKey="speed_avg" fill="#8884d8" />
        <XAxis dataKey="ts" />
        <YAxis width={45} />
      </BarChart>
    </AutoSizer>
  )
  //~ return history.length
}

const RemainingTime = ({percentage, label, startTime, total}) => {
  let [history, setHistory] = useResettableState(
    [{ts: parseISO(startTime), percentage: 0, total}],
    [startTime]
  )
  // needs more logic
  //~ let [history, setHistory] = useState([])
  useEffect(() => {
    if (history.at(-1)?.percentage !== percentage) {
      setHistory([...history, {ts: new Date(), percentage}])
    }
  }, [percentage])
  let remaining = useMemo(() => {
    // take last N but ignore inital 0 percentage
    let last_hist = history.slice(-20).slice(1)
    if (last_hist.length > 1) {
      let delta_perc = last_hist.at(-1).percentage - last_hist.at(0).percentage
      let delta_secs = differenceInSeconds(last_hist.at(-1).ts, last_hist.at(0).ts)
      let perc_second = delta_perc / delta_secs
      let remaining_perc = 100 - last_hist.at(-1).percentage
      if (perc_second > 0 && remaining_perc > 0) {
        let remaining_sec = remaining_perc / perc_second
        //~ log.x({delta_perc, delta_secs, perc_second, remaining_perc, remaining_sec})
        return ` ~${formatSeconds(remaining_sec)}`
      }
    }
  }, [history])

  if (label == 'domain') {
    // domains are not uniform enough
    return null
  }
  let countRemaining = Math.ceil((total * (100 - percentage)) / 100)

  let trigger = (
    <span title={countRemaining} style={{cursor: 'pointer'}}>
      {remaining}
    </span>
  )
  return (
    <ResizableDialog
      trigger={trigger}
      header={`History (${history.length}) (moving average 5)`}
      closeOnEsc
      overflow="hidden"
    >
      <Timings history={history} movAvg={5} />
    </ResizableDialog>
  )
}

export const EventTypeIndicators = ({basic = false, error, warn, event}) => {
  if (basic) {
    return (
      <>
        {error > 0 && <span style={{backgroundColor: '#FBB'}}>&nbsp;{error}&nbsp;</span>}
        {warn > 0 && <span style={{backgroundColor: '#FFB'}}>&nbsp;{warn}&nbsp;</span>}
        {event > 0 && <span style={{backgroundColor: '#CFC'}}>&nbsp;{event}&nbsp;</span>}
      </>
    )
  } else {
    return mapItems(
      {error, warn, event},
      (name, count) => buildCounter(name, count),
      (name, count) => count > 0
    )
  }
}

export const JobReport = ({name, ojid, jobinfo}) => {
  let apiurl = useSelector(Sel.apiurl)
  let [pageSize, setPageSize] = useState(0)
  let [more, setMore] = useState(false)
  let evdata = useJobEvents(ojid)
  let last_jobinfo = useSelector(root => root.data.job.info[ojid])

  // last_jobinfo has newer event counts and progress values
  jobinfo = useMemo(() => {
    let newer = {}
    if (last_jobinfo) {
      // only pick some keys (kwds is always null on last_jobinfo)
      let {events, progress, state, logSize} = last_jobinfo
      newer = {events, progress, state, logSize}
    }
    return {...jobinfo, ...newer}
  }, [jobinfo, last_jobinfo])

  //~ log.x('asdasd', jobinfo)
  //~ let [rawReport, setRawReport] = usePersistent('rawReport', false)
  let [rawReport, setRawReport] = useState(false)
  let [debugEvents, setDebugEvents] = usePersistent('debugEvents', false)

  let evt_counts = {}
  if (!evdata.loading) {
    evt_counts = {
      error: evdata.byType.error.length,
      warn: evdata.byType.warning.length,
    }
    evt_counts.event = evdata.events.length - evt_counts.error - evt_counts.warn
  } else {
    evdata = {events: [], byType: {}, states: []}
  }

  let typeColor = t => get({error: 'red', warning: 'orange'}, t, 'green')

  let build = (evt, i) => {
    return (
      <li key={i}>
        <span>{formatTime(evt.time)} </span>
        <span style={{color: typeColor(evt.type)}}>{evt.type} </span>
        {'-'}
        <span> {evt.message}</span>
      </li>
    )
  }

  let columns = [
    {
      id: 'time',
      Header: 'Time',
      Cell: ({value, aggregated}) => (aggregated ? null : formatTime(value)),
      accessor: 'time',
      width: 75,
      sortable: true,
      aggregate: (vals, rows) => '',
    },
    {
      id: 'type',
      Header: <span>Type</span>,
      accessor: f => f.type,
      style: {whiteSpace: 'unset'},
      groupable: true,
    },
    {
      id: 'message',
      Header: 'Message',
      Cell: ({value, aggregated}) => {
        if (aggregated) return null
        let {msg, otid} = value
        // XXX: odid missing - maybe we should watch for a preceeding odid transporting event
        if (otid) {
          return (
            <Link id={otid} classname="tree" tabname="id">
              {msg}
            </Link>
          )
        }
        return msg
      },
      filterMethod: (filter, rows) =>
        matchSorter(rows, filter.value, {keys: ['_original.message']}),
      filterAll: true, // for giving rows to matchSorter

      accessor: f => ({msg: f.message, otid: f.otid}),
      style: {whiteSpace: 'unset'},
      aggregate: (vals, rows) => '',
    },
  ]
  if (debugEvents) {
    columns.push({
      id: 'evt',
      Header: 'Event',
      Cell: ({value, aggregated}) => {
        if (aggregated) return null
        return mapItems(value, (k, v) => (
          <div key={k} style={{lineHeight: '1rem'}}>
            <tt>
              {k}: {isArray(v) ? v.map(e => String(e)).join(', ') : v}
            </tt>
          </div>
        ))
      },
      accessor: f => {
        let {message, time, ...rest} = f
        return rest
      },
      style: {whiteSpace: 'unset'},
      aggregate: (vals, rows) => '',
    })
  }
  let buildState = (dt, state, is_last) => (
    <XMenu.Item key={state}>
      <Icon
        {...(state_icons[state] || {name: 'info'})}
        loading={is_last && state_icons[state]?.loading}
      />
      {'  '}
      {formatTime(dt)} {state}
    </XMenu.Item>
  )

  let counts = dict(
    [...(debugEvents ? ['event'] : []), 'warn', 'error'].map(name => [
      name,
      get(jobinfo, `events.${name}`, evt_counts[name]),
    ])
  )
  let counters = <EventTypeIndicators {...counts} />
  let states = evdata.states.map(([dt, state], i) =>
    buildState(dt, state, i == evdata.states.length - 1)
  )

  let customRenderProps = {events: evdata.events, eventTypes: evdata.byType, ojid, jobinfo}

  let customCompactRenderer = JobTemplates[name]?.compact
  let compactLine = null

  let report = null
  let customReportRenderer = JobTemplates[name]?.report
  let customReportActive = customReportRenderer && !rawReport
  if (customReportActive) {
    report = React.createElement(customReportRenderer, customRenderProps)
  } else {
    if (customCompactRenderer) {
      compactLine = React.createElement(customCompactRenderer, customRenderProps)
    }

    report = (
      <TranslatedTable
        sortable={false}
        className="-striped"
        showPaginationTop={evdata.events.length >= 20}
        showPaginationBottom={false}
        showPagination={true}
        data={evdata.events}
        columns={columns}
        filterable={true}
        defaultFilterMethod={(filter, row) =>
          String(row[filter.id])
            .toLowerCase()
            .includes(filter.value)
        }
        defaultSorted={[
          {
            id: 'time',
            desc: true,
          },
        ]}
        pageSizeOptions={[20, 50, 100, 500, 1000, 5000]}
        onPageSizeChange={pageSize => setPageSize(pageSize)}
        getTrProps={(state, rowInfo, column) => {
          if (!rowInfo) return {}
          let rowColor = null
          if (rowInfo.row.type === 'warning') rowColor = 'orange'
          if (rowInfo.row.type === 'error') rowColor = 'red'
          return {
            style: {
              color: rowColor,
            },
          }
        }}
      />
    )
  }
  let {progress_init = [], progress_step = []} = evdata?.byType ?? {}

  let progress2label = useMemo(
    () =>
      dict(
        progress_init.map(({progress_id, label, total, time}) => [
          progress_id,
          {label, total, time},
        ])
      ),
    [size(progress_init)]
  )
  let progressid2steps = useMemo(() => groupBy(progress_step, step => step.progress_id), [
    progress_step,
  ])

  let domains = useSelector(Sel.domains)
  let label_progress = (progress_id, percentage, {label, total, time}) => {
    let last_step = progressid2steps[progress_id]?.at(-1)
    let step_label = last_step?.step_label

    let step_title = null
    if (label == 'domain' && step_label) {
      let data = domains.data[step_label]
      step_title = data.webname || data.description || step_label
    }
    return (
      <div style={{marginTop: '-4px', fontSize: '0.7em'}}>
        {step_label && (
          <span style={{float: 'right'}} title={step_title}>
            {step_label}
          </span>
        )}
        {label ? `${ucFirst(label)} (${total})` : total}
        <RemainingTime percentage={percentage} label={label} startTime={time} total={total} />
      </div>
    )
  }

  let progress = null
  if (jobinfo?.state == 'RUNNING' && (jobinfo?.progress || []).some(e => e)) {
    progress = jobinfo.progress.map((p, i) =>
      // first check some visibility conditions
      p && (p < 100 || (i < 4 && jobinfo.progress[i + 1])) ? (
        <Progress
          key={i}
          label={label_progress(i, p, progress2label[i] ?? {})}
          percent={p}
          precision={0}
          progress
          active
          size="small"
        />
      ) : null
    )
  }
  return (
    <Container fluid>
      <AutoPortal target="JobInfoMenuPortal">
        <XMenu padded>
          <XMenu.Item
            icon="binoculars"
            onClick={() => window.open(apiurl.href(`/log/serveJobLog/${jobinfo.ojid}`), 'joblog')}
            title="Show log of job"
          />

          <XMenu.Item
            icon="bug"
            active={debugEvents}
            onClick={() => setDebugEvents(!debugEvents)}
          />

          {!!customReportRenderer && (
            <XMenu.Item icon="table" active={!rawReport} onClick={() => setRawReport(!rawReport)} />
          )}
        </XMenu>
      </AutoPortal>
      {!customReportActive && (
        <XMenu padded style={{marginBottom: '0.5rem'}}>
          {more ? (
            <XMenu.Item icon="close" onClick={() => setMore(!more)} />
          ) : (
            <XMenu.Item content="..." onClick={() => setMore(!more)} />
          )}
          {more ? states : states[states.length - 1]}
          {compactLine && <Segment textAlign="right">{compactLine}</Segment>}
          {counters}
        </XMenu>
      )}
      {progress}
      {report}
    </Container>
  )
}
